// Ladder challenge "When & where": one state machine for the challenge page,
// the ladder's challenge rows and the accept dialog (spec R4, Grant 2026-09-22:
// "make sure this is as clear as fuck ... doing one action must never
// erroneously accept a challenge"). The app carries a Dart copy with the same
// states and copy. Unit tested in tests/unit/challenge-schedule.test.ts.
//
// States:
//   S1  pending, time suggested (future)       S4  accepted, proposal awaiting the other side
//   S1p pending, suggested time has passed     S4p as S4, the proposed time has passed
//   S2  pending, no suggestion                 S5  confirmed by both sides
//   S3  accepted, no time                      S6  confirmed, set by a club admin
//   S7  played / result entered                S8  rejected, expired or cancelled (no card)
//
// Pure: no Nuxt, no luxon. Times come back raw (as the API sent them) for the
// caller to format; "passed" is decided against `now`.
//
// Nothing here offers a schedule, record-result or live-score action on a
// pending challenge: only an explicit Accept accepts one.

/** API timestamps are UTC, usually without a zone designator. */
export function toMs(d) {
    if (d == null || d === '') return null;
    if (d instanceof Date) return d.getTime();
    if (typeof d === 'number') return d;
    const s = String(d);
    const hasZone = /(Z|[+-]\d\d:?\d\d)$/i.test(s);
    const ms = Date.parse(hasZone || !s.includes('T') ? s : `${s}Z`);
    return Number.isNaN(ms) ? null : ms;
}

/** Equal to the minute: a time rebuilt from date + HH:mm still matches. */
export function sameMinute(a, b) {
    const x = toMs(a);
    const y = toMs(b);
    if (x == null || y == null) return false;
    return Math.abs(x - y) < 60_000;
}

// The API sends each side's display name on the challenge
// (challengingTeamDisplayName / challengedTeamDisplayName: the ladder entry
// name if set, else the team name — singles the player, doubles "A + B") and
// whether nobody on a side has an account (…TeamAdminControlled). Read here,
// in one place; when absent the team's own fields are used.
const DISPLAY_NAME_FIELDS = ['displayName'];
const ADMIN_CONTROLLED_FIELDS = ['adminControlled'];

/** A challenge side's display name: 'challenger' | 'challenged'. */
export function challengeSideName(challenge, side, fallback) {
    const c = challenge ?? {};
    const own = side === 'challenger' ? c.challengingTeamDisplayName : c.challengedTeamDisplayName;
    if (own) return own;
    return sideName(side === 'challenger' ? c.challengingTeam : c.challengedTeam, fallback);
}

/** Nobody on this side of the challenge has an account (API flag; missing = false). */
export function challengeSideAdminControlled(challenge, side) {
    const c = challenge ?? {};
    const flag = side === 'challenger' ? c.challengingTeamAdminControlled : c.challengedTeamAdminControlled;
    return isAdminControlledSide(side === 'challenger' ? c.challengingTeam : c.challengedTeam, flag);
}

/** Display name of a side: the API's display name, its name, else its players, else the role. */
export function sideName(team, fallback) {
    for (const k of DISPLAY_NAME_FIELDS) if (team?.[k]) return team[k];
    if (team?.name) return team.name;
    const players = (team?.players ?? []).map(p => p?.name || [p?.firstName, p?.lastName].filter(Boolean).join(' ')).filter(Boolean);
    return players.length ? players.join(' & ') : fallback;
}

/**
 * Is this side run by admins (no account holders)? Only the API's flag
 * decides; missing = false (today's behaviour). `challengeFlag` is a
 * challenge-level flag for the same thing, if the API puts it there.
 */
export function isAdminControlledSide(team, challengeFlag) {
    if (typeof challengeFlag === 'boolean') return challengeFlag;
    for (const k of ADMIN_CONTROLLED_FIELDS) if (typeof team?.[k] === 'boolean') return team[k];
    return false;
}

/** A side with more than one player: its members read "your side". */
export function isTeamSide(team) {
    return (team?.players?.length ?? 0) > 1;
}

/**
 * The ladder's decline penalty, worded for who it applies to: `who` null =
 * "you" (the challenged side reading it), else that side's name.
 */
export function rejectionPenaltyText(ladder, who = null) {
    if (!ladder || !ladder.challengeRejectionRule) return null;
    const places = ladder.challengeRejectionMoveDownPlaces;
    switch (ladder.challengeRejectionRule) {
        case 1: return `${who ?? 'You'} will be moved down ${places} place${places > 1 ? 's' : ''}`;
        case 2: return `${who ?? 'You'} will forfeit the match`;
        default: return null;
    }
}

/**
 * The note on a club admin's "Cancel challenge?" confirmation (owner decision
 * 29 Sep 2026): cancelling skips the ladder's decline penalty, so say so.
 * Only while the challenge waits for an answer (declining is only possible
 * then), only for an admin cancelling someone else's challenge (the
 * challenger's own cancel keeps its wording), and only when the ladder has a
 * penalty: move down N places (1, with N > 0) or forfeit (2).
 */
export function adminCancelPenaltyNote(ladder, viewer, phase) {
    if (!viewer?.isAdmin || viewer?.isChallenger || phase !== 'pending') return null;
    const places = ladder?.challengeRejectionMoveDownPlaces;
    switch (ladder?.challengeRejectionRule) {
        case 1:
            if (!(places > 0)) return null;
            return `Cancelling removes this challenge with no ladder penalty. Declining it would move the challenged side down ${places} place${places > 1 ? 's' : ''}.`;
        case 2:
            return 'Cancelling removes this challenge with no ladder penalty. Declining it would count as a forfeit.';
        default:
            return null;
    }
}

/**
 * The line on the challenged side's "Cancel challenge?" confirmation (owner
 * decision 3 Nov 2026): once they have accepted, cancelling counts as
 * declining, so the ladder's decline penalty applies (the API applies it).
 * Only for the challenged side cancelling as themselves (a club admin cancels
 * as an admin, with no penalty), only once accepted and unplayed (while
 * pending they decline instead), and only when the ladder has a penalty:
 * move down N places (1, with N > 0) or forfeit (2). The app's
 * challenge_schedule_state.dart carries the same words.
 */
export function challengedCancelPenaltyNote(ladder, viewer, phase) {
    if (!viewer?.isChallenged || viewer?.isAdmin || phase !== 'accepted') return null;
    const places = ladder?.challengeRejectionMoveDownPlaces;
    switch (ladder?.challengeRejectionRule) {
        case 1:
            if (!(places > 0)) return null;
            return `Cancelling counts as declining, so your side moves down ${places} place${places > 1 ? 's' : ''}.`;
        case 2:
            return 'Cancelling counts as declining, so it counts as a forfeit.';
        default:
            return null;
    }
}

/** "just now" / "5 min ago" / "2h ago" / "3d ago". */
export function timeAgo(when, now = Date.now()) {
    const ms = toMs(when);
    if (ms == null) return '';
    const mins = Math.floor((toMs(now) - ms) / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
}

const isPassed = (when, now) => {
    const ms = toMs(when);
    return ms != null && ms < toMs(now ?? Date.now());
};

/** Which role key picks the copy: a player's own copy wins over admin copy. */
export function viewerRole(viewer) {
    if (viewer?.isChallenger) return 'challenger';
    if (viewer?.isChallenged) return 'challenged';
    if (viewer?.isAdmin) return 'admin';
    return 'other';
}

/**
 * Phase of the challenge itself: 'pending' | 'accepted' | 'played' | 'closed'.
 * A fixture without an acceptance (older data, or a result recorded by the
 * challenged side) counts as accepted.
 */
export function challengePhase(challenge, fixture, now) {
    const c = challenge ?? {};
    const f = fixture === undefined ? (c.fixture ?? null) : fixture;
    if (c.deletedAt || c.rejectedAt) return 'closed';
    if (f && (f.playedAt || f.homeTeamResult != null || f.awayTeamResult != null)) return 'played';
    if (c.acceptedAt || f) return 'accepted';
    if (c.expiresAt && isPassed(c.expiresAt, now)) return 'closed';
    return 'pending';
}

/**
 * The open proposal on an accepted challenge's fixture, or null.
 * {when (raw), side: 'challenger'|'challenged'}
 */
export function openProposal(challenge, fixture) {
    const f = fixture;
    if (!f || f.scheduledAt) return null;
    const home = f.homeTeamScheduledAt ?? null;
    const away = f.awayTeamScheduledAt ?? null;
    if (!home && !away) return null;
    // Only one side holds a proposal at a time; if both are set (older data)
    // the away side answered last.
    const onAway = !!away;
    const when = onAway ? away : home;
    const challengerId = challenge?.challengingTeamId ?? challenge?.challengingTeam?.id;
    const homeId = f.homeTeamId ?? challengerId; // challenger plays at home
    const teamId = onAway ? (f.awayTeamId ?? null) : homeId;
    let side;
    if (teamId != null && challengerId != null) side = teamId === challengerId ? 'challenger' : 'challenged';
    else side = onAway ? 'challenged' : 'challenger';
    return {when, side};
}

/**
 * The whole card for one viewer.
 *
 * @param challenge  the ladder challenge (API response)
 * @param fixture    its fixture; `undefined` = challenge.fixture
 * @param viewer     {isChallenger, isChallenged, isAdmin}
 * @param now        Date | ms | ISO; defaults to the clock
 * @returns {{
 *   state: string, show: boolean, phase: string,
 *   chip: {label: string, kind: 'warn'|'ok'|'muted'}|null,
 *   when: string|null, whenPassed: boolean, area: string|null,
 *   emptyText: string|null, playedAt: string|null,
 *   proposer: 'challenger'|'challenged'|null,
 *   lines: {challenger: string|null, challenged: string|null, admin: string|null, other: string|null},
 *   line: string|null, role: string,
 *   actions: Array<{id: string, label: string, value?: string, playingAreaId?: string|null}>,
 * }}
 */
export function challengeSchedule(challenge, fixture, viewer = {}, now = Date.now()) {
    const c = challenge ?? {};
    const f = fixture === undefined ? (c.fixture ?? null) : fixture;
    const v = {isChallenger: !!viewer?.isChallenger, isChallenged: !!viewer?.isChallenged, isAdmin: !!viewer?.isAdmin};
    const role = viewerRole(v);
    const isPlayer = v.isChallenger || v.isChallenged;
    const challenger = challengeSideName(c, 'challenger', 'The challenger');
    const challenged = challengeSideName(c, 'challenged', 'the challenged side');
    const phase = challengePhase(c, f, now);
    // In doubles a proposal may be a teammate's: "Your side suggested this".
    const you = (side) => isTeamSide(side === 'challenger' ? c.challengingTeam : c.challengedTeam) ? 'Your side' : 'You';

    const out = {
        state: 'S8', show: false, phase, role,
        chip: null, when: null, whenPassed: false, area: null,
        emptyText: null, playedAt: null, proposer: null,
        changedAt: null, changedHint: null,
        lines: {challenger: null, challenged: null, admin: null, other: null},
        line: null, actions: [],
    };
    const done = () => {
        out.line = out.lines[role];
        return out;
    };

    if (phase === 'closed') return done();

    // ---- pending: only an explicit Accept moves it on ----
    if (phase === 'pending') {
        out.show = true;
        const suggested = c.suggestedDate ?? null;
        if (suggested) {
            out.when = suggested;
            out.area = c.suggestedPlayingAreaName ?? null;
            out.proposer = 'challenger';
            if (isPassed(suggested, now)) {
                out.state = 'S1p';
                out.whenPassed = true;
                out.chip = {label: 'Time passed', kind: 'muted'};
                out.lines = {
                    challenger: `${you('challenger') === 'You' ? 'Your' : "Your side's"} suggested time has passed. Suggest a new one so ${challenged} can agree to it.`,
                    challenged: `${challenger}'s suggested time has passed. Accept, then suggest a new time.`,
                    admin: `${challenger}'s suggested time has passed. A new time can be agreed after ${challenged} accepts.`,
                    other: `${challenger}'s suggested time has passed. A new time can be agreed after ${challenged} accepts.`,
                };
            } else {
                out.state = 'S1';
                out.chip = {label: 'Suggested', kind: 'warn'};
                out.lines = {
                    challenger: `${you('challenger')} suggested this. Waiting for ${challenged} to accept. ${challenged} can agree to it or suggest another time.`,
                    challenged: `${challenger} suggested this. Nothing is fixed until you accept. You can agree to it, suggest another time, or decide later.`,
                    admin: `Suggested by ${challenger} · waiting for ${challenged} to accept.`,
                    other: `Suggested by ${challenger} · waiting for ${challenged} to accept.`,
                };
            }
        } else {
            out.state = 'S2';
            out.emptyText = 'No time suggested yet';
            out.lines = {
                challenger: `You can suggest a time while you wait, or arrange one after ${challenged} accepts.`,
                challenged: 'Accept, then agree a time together.',
                admin: `Waiting for ${challenged} to accept.`,
                other: `Waiting for ${challenged} to accept.`,
            };
        }
        out.actions = pendingActions(v, challenger, challenged, out.state);
        // The challenger changed their suggestion after sending: the challenged
        // side is told, so the time they're looking at isn't a surprise.
        if (c.suggestionChangedAt && c.suggestedDate && v.isChallenged) {
            out.changedAt = c.suggestionChangedAt;
            out.changedHint = `Suggested time changed · ${timeAgo(c.suggestionChangedAt, now)}`;
        }
        return done();
    }

    // ---- played: the result card carries the rest ----
    if (phase === 'played') {
        out.state = 'S7';
        out.show = true;
        out.playedAt = f?.playedAt ?? f?.scheduledAt ?? null;
        out.area = f?.playingAreaName ?? null;
        if (isPlayer || v.isAdmin) out.actions = [{id: 'recordResult', label: 'Result'}];
        return done();
    }

    // ---- accepted: the schedule handshake ----
    out.show = true;
    out.area = f?.playingAreaName ?? null;
    const scheduleActions = [];
    if (f?.scheduledAt) {
        out.when = f.scheduledAt;
        out.chip = {label: 'Confirmed', kind: 'ok'};
        const byAdmin = f.scheduleSetByAdmin === true;
        out.state = byAdmin ? 'S6' : 'S5';
        const text = byAdmin ? 'Set by a club admin.' : 'Agreed by both sides.';
        out.lines = {challenger: text, challenged: text, admin: text, other: text};
        if (isPlayer || v.isAdmin) scheduleActions.push({id: 'schedule', label: 'Reschedule', mode: 'reschedule'});
    } else {
        const proposal = openProposal(c, f);
        if (proposal) {
            out.when = proposal.when;
            out.proposer = proposal.side;
            const proposerName = proposal.side === 'challenger' ? challenger : challenged;
            const otherName = proposal.side === 'challenger' ? challenged : challenger;
            if (isPassed(proposal.when, now)) {
                out.state = 'S4p';
                out.whenPassed = true;
                out.chip = {label: 'Time passed', kind: 'muted'};
                const text = 'This suggested time has passed. Suggest a new one.';
                out.lines = {
                    challenger: text, challenged: text,
                    admin: 'This suggested time has passed. Suggest or set a new one.',
                    other: 'This suggested time has passed.',
                };
                if (v.isAdmin) scheduleActions.push({id: 'schedule', label: 'Set a time', mode: 'set'});
                else if (isPlayer) scheduleActions.push({id: 'schedule', label: 'Suggest a time', mode: 'suggest'});
            } else {
                out.state = 'S4';
                out.chip = {label: 'Proposed', kind: 'warn'};
                const mine = (side) => side === proposal.side;
                const playerLine = (side) => mine(side)
                    ? `${you(side)} suggested this. Waiting for ${otherName} to confirm.`
                    : `${proposerName} suggested this. Confirm it or suggest another time.`;
                out.lines = {
                    challenger: playerLine('challenger'),
                    challenged: playerLine('challenged'),
                    admin: `Suggested by ${proposerName} · waiting for ${otherName} to confirm.`,
                    other: `Suggested by ${proposerName} · waiting for ${otherName} to confirm.`,
                };
                const viewerSide = v.isChallenger ? 'challenger' : v.isChallenged ? 'challenged' : null;
                const confirm = {id: 'confirmTime', label: 'Confirm time', value: proposal.when, playingAreaId: f?.playingAreaId ?? null};
                if (viewerSide && !mine(viewerSide)) {
                    // The other player: one tap confirms exactly what was proposed.
                    scheduleActions.push(confirm);
                    scheduleActions.push(v.isAdmin
                        ? {id: 'schedule', label: 'Set another time', mode: 'setAnother'}
                        : {id: 'schedule', label: 'Suggest another', mode: 'suggestAnother'});
                } else if (viewerSide) {
                    // The proposer. An admin's change is confirmed straight away.
                    scheduleActions.push(v.isAdmin
                        ? {id: 'schedule', label: 'Set a time', mode: 'set'}
                        : {id: 'schedule', label: 'Change time', mode: 'change'});
                } else if (v.isAdmin) {
                    scheduleActions.push({id: 'schedule', label: 'Set a time', mode: 'set'});
                }
            }
        } else {
            out.state = 'S3';
            out.chip = {label: 'Not scheduled', kind: 'muted'};
            out.emptyText = 'No time agreed yet';
            out.lines = {
                challenger: v.isAdmin ? 'Either of you can suggest a time or, as a club admin, set one.' : 'Either of you can suggest a time.',
                challenged: v.isAdmin ? 'Either of you can suggest a time or, as a club admin, set one.' : 'Either of you can suggest a time.',
                admin: 'Either side can suggest a time, or set one.',
                other: 'Either side can suggest a time.',
            };
            if (v.isAdmin) scheduleActions.push({id: 'schedule', label: 'Set a time', mode: 'set'});
            else if (isPlayer) scheduleActions.push({id: 'schedule', label: 'Suggest a time', mode: 'suggest'});
        }
    }
    out.actions = scheduleActions;
    if (isPlayer || v.isAdmin) {
        out.actions.push({id: 'recordResult', label: 'Record result'});
        out.actions.push({id: 'liveScore', label: 'Live score'});
        // Accepted and unplayed: either side or a club admin can cancel (owner,
        // 3 Nov 2026, reversing the 22 Sep "admins only once arranged" rule).
        // Once a match exists the unplayed match is deleted with it. For the
        // challenged side (not as an admin) it counts as declining: the API
        // applies the ladder's decline penalty, challengedCancelPenaltyNote says so.
        if (f) out.actions.push({id: 'cancel', label: 'Cancel challenge', deletesMatch: true});
        else out.actions.push({id: 'cancel', label: 'Cancel challenge'});
    }
    return done();
}

function pendingActions(v, challenger, challenged, state) {
    if (v.isChallenged) {
        // Their own answer, admin or not. Their route to another time is
        // Accept → "Suggest another time", never editing the suggestion.
        // A club admin on the challenged side can also cancel it, as any
        // admin can (the API allows it; declining would apply the penalty).
        return [
            ...(v.isAdmin ? [{id: 'cancel', label: 'Cancel challenge'}] : []),
            {id: 'decline', label: 'Decline'},
            {id: 'accept', label: 'Accept challenge'},
        ];
    }
    const actions = [];
    // The challenger (or an admin, for them) can change, set or remove the
    // suggested time while waiting — never accepting anything. A passed
    // suggestion nudges: "Suggest a new time" is the primary action then.
    if (v.isChallenger || v.isAdmin) {
        const own = v.isChallenger;
        const label = {
            S1: own ? 'Change suggested time' : `Change ${challenger}'s suggested time`,
            S1p: own ? 'Suggest a new time' : `Suggest a new time for ${challenger}`,
            S2: own ? 'Suggest a time' : `Suggest a time for ${challenger}`,
        }[state];
        if (label) {
            actions.push({
                id: 'changeSuggestion', label, primary: state === 'S1p',
                // S1: the dialog also offers "Remove suggested time"
                canRemove: state === 'S1' || state === 'S1p',
            });
        }
        actions.push({id: 'cancel', label: 'Cancel challenge'});
    }
    if (v.isAdmin) {
        actions.push({id: 'declineOnBehalf', label: `Decline on ${challenged}'s behalf`});
        actions.push({id: 'acceptOnBehalf', label: `Accept on ${challenged}'s behalf`});
    }
    return actions;
}

/**
 * The same card state for a LADDER match shown as a match (fixture page, match
 * cells), so its words match the challenge page. `fixture.ladderChallenge` is
 * on the fixture detail; list rows lack it, so the challenger is taken to be
 * the home side (the convention every ladder match is created with).
 * Returns null for non-ladder matches.
 */
export function ladderFixtureSchedule(fixture, viewer = {}, now = Date.now()) {
    if (!fixture?.ladderChallengeId) return null;
    const lc = fixture.ladderChallenge ?? {};
    const challengingTeamId = lc.challengingTeamId ?? fixture.homeTeamId ?? fixture.homeTeam?.id;
    const challengedTeamId = lc.challengedTeamId ?? fixture.awayTeamId ?? fixture.awayTeam?.id;
    const teamFor = (id) => (id != null && id === (fixture.homeTeamId ?? fixture.homeTeam?.id)) ? fixture.homeTeam : fixture.awayTeam;
    const challenge = {
        ...lc,
        challengingTeamId, challengedTeamId,
        challengingTeam: lc.challengingTeam ?? teamFor(challengingTeamId),
        challengedTeam: lc.challengedTeam ?? teamFor(challengedTeamId),
        // a match exists: the challenge was accepted (explicitly or implicitly)
        acceptedAt: lc.acceptedAt ?? fixture.createdAt ?? true,
        deletedAt: null, rejectedAt: null,
    };
    return challengeSchedule(challenge, fixture, viewer, now);
}

/** The viewer's roles on a ladder match, from their team id. */
export function ladderFixtureViewer(fixture, myTeamId, isClubAdmin) {
    const lc = fixture?.ladderChallenge ?? {};
    const challengingTeamId = lc.challengingTeamId ?? fixture?.homeTeamId ?? fixture?.homeTeam?.id;
    const challengedTeamId = lc.challengedTeamId ?? fixture?.awayTeamId ?? fixture?.awayTeam?.id;
    return {
        isChallenger: myTeamId != null && myTeamId === challengingTeamId,
        isChallenged: myTeamId != null && myTeamId === challengedTeamId,
        isAdmin: !!isClubAdmin,
    };
}

/**
 * The accept dialog's time choices (spec R2).
 * @param viewer {isChallenged, isAdmin} — the accepter; admin not on the
 *               challenged side accepts "on behalf".
 */
export function acceptTimeOptions(challenge, viewer = {}, now = Date.now()) {
    const c = challenge ?? {};
    const challenger = challengeSideName(c, 'challenger', 'The challenger');
    const challenged = challengeSideName(c, 'challenged', 'the challenged side');
    const onBehalf = !!viewer?.isAdmin && !viewer?.isChallenged;
    // The API checks club admin before team membership: any admin accepter's
    // time is confirmed (and flagged set-by-admin), even the challenged side's own.
    const asAdmin = !!viewer?.isAdmin;
    // Nobody on an admin-controlled side (no account holders) could confirm a
    // time later, so "Decide later" is not offered for it.
    const adminControlled = challengeSideAdminControlled(c, 'challenged') || viewer?.challengedAdminControlled === true;
    const adminOwnTime = `As a club admin, your time is confirmed straight away (${challenger} can reschedule).`;
    const labels = onBehalf
        ? {agree: `Agree to ${challenger}'s time`, other: 'Set another time', later: 'Decide later'}
        : {agree: 'That works for me', other: 'Suggest another time', later: 'Decide later'};
    const suggested = c.suggestedDate ?? null;
    if (!suggested) {
        return {
            hasSuggestion: false, suggestionPassed: false, onBehalf, adminControlled,
            choices: [], defaultChoice: null, labels,
            suggestLabel: onBehalf ? 'Set a time for the match?' : 'Suggest a time for the match?',
            suggestHint: asAdmin ? adminOwnTime : `${challenger} will need to confirm your time.`,
            passedNote: null,
            hints: {},
        };
    }
    const passed = isPassed(suggested, now);
    if (passed) labels.other = onBehalf ? 'Set a new time' : 'Suggest a new time';
    const confirmer = viewer?.isChallenged ? 'you' : `${challenged}'s side`;
    const hints = asAdmin
        ? {
            agree: 'The match is confirmed for this time.',
            other: adminOwnTime,
            later: passed
                ? 'No time is set yet. Either side can suggest one after you accept.'
                : `${challenger}'s suggested time stays open for ${confirmer} to confirm.`,
        }
        : {
            agree: 'The match is confirmed for this time.',
            other: `${challenger} will need to confirm your time.`,
            later: passed
                ? 'No time is set yet. Suggest one from the challenge page when you know.'
                : `${challenger}'s suggestion stays open. Confirm it or suggest another from the challenge page.`,
        };
    let choices = passed ? ['other', 'later'] : ['agree', 'other', 'later'];
    if (adminControlled) choices = choices.filter(ch => ch !== 'later');
    return {
        hasSuggestion: true,
        suggestionPassed: passed,
        onBehalf,
        adminControlled,
        // A time that has gone can't be agreed to.
        choices,
        defaultChoice: passed ? 'other' : 'agree',
        labels,
        suggestLabel: null,
        suggestHint: null,
        passedNote: passed ? 'The suggested time has passed. Suggest a new one.' : null,
        hints,
    };
}

/** Request field for "the suggested time I agreed to" (API contract name). */
export const AGREED_SUGGESTION_FIELD = 'agreedSuggestedDate';

/**
 * The challenger's change-suggestion body (PUT ladderChallenges/{id}/suggestion).
 * `remove` sends no time and no court.
 */
export function buildSuggestionRequest(form, toDbDate, {remove = false} = {}) {
    if (remove) return {suggestedDate: null, playingAreaId: null};
    return {
        suggestedDate: form?.date && form?.time ? toDbDate(form.date, form.time) : null,
        playingAreaId: form?.playingAreaId || null,
    };
}

/** Toast after a suggestion change, naming who has been told. */
export function suggestionSavedMessage(challenge, {removed = false, hadSuggestion = false} = {}) {
    const challenged = challengeSideName(challenge, 'challenged', 'the other side');
    if (removed) return `Suggested time removed. ${challenged} has been told`;
    return hadSuggestion
        ? `Suggested time changed. ${challenged} has been told`
        : `Time suggested. ${challenged} has been told`;
}

/**
 * The `/accept` body. `form`: {timeChoice, suggestDate, date, time,
 * playingAreaId, acceptanceMessage}. `toDbDate(date, time)` turns the picked
 * local date + HH:mm into the API's UTC value.
 *
 * - agree  → the challenger's time sent back exactly as stored; court left to
 *            the API (the challenger's), so a court deleted since still works.
 * - other  → the picked time and court (null = no court), flagged specified.
 * - later  → no time: the challenger's proposal stays open.
 */
export function buildAcceptRequest(challenge, form, toDbDate, now = Date.now()) {
    const c = challenge ?? {};
    const data = {
        ladderChallengeId: c.id,
        acceptanceMessage: form?.acceptanceMessage ?? null,
        accept: true,
    };
    const opts = acceptTimeOptions(c, {}, now);
    const choice = opts.hasSuggestion
        ? (opts.choices.includes(form?.timeChoice) ? form.timeChoice : opts.defaultChoice)
        : (form?.suggestDate ? 'other' : 'later');
    if (choice === 'agree') {
        data.suggestedDate = c.suggestedDate;
        // The suggestion the accepter saw: if the challenger changed it
        // meanwhile, the API refuses rather than agreeing to a time unseen.
        data[AGREED_SUGGESTION_FIELD] = c.suggestedDate;
    } else if (choice === 'other' && form?.date && form?.time) {
        data.suggestedDate = toDbDate(form.date, form.time);
        data.playingAreaId = form.playingAreaId || null;
        data.playingAreaSpecified = true;
    }
    return data;
}

/** "within 2 days, 3 hours" until `expiresAt`, from the challenge's own expiry. */
export function expiryCountdown(expiresAt, now = Date.now()) {
    const end = toMs(expiresAt);
    if (end == null) return null;
    let s = Math.floor((end - toMs(now)) / 1000);
    if (s < 0) return 'Challenge deadline has passed';
    const days = Math.floor(s / 86400); s -= days * 86400;
    const hours = Math.floor(s / 3600); s -= hours * 3600;
    const minutes = Math.floor(s / 60); s -= minutes * 60;
    const unit = (n, one) => `${n} ${one}${n === 1 ? '' : 's'}`;
    if (days >= 1) return `within ${unit(days, 'day')}, ${unit(hours, 'hour')}`;
    if (hours >= 1) return `within ${unit(hours, 'hour')}, ${unit(minutes, 'minute')}`;
    if (minutes >= 1) return `within ${unit(minutes, 'minute')}, ${unit(s, 'second')}`;
    return `within ${unit(s, 'second')}`;
}

/**
 * A schedule value to send from the dialog: when the picked minute equals the
 * other side's proposal, send the proposal exactly as stored (a confirm).
 */
export function scheduleValueToSend(pickedDbValue, otherSideProposal) {
    if (otherSideProposal && sameMinute(pickedDbValue, otherSideProposal)) return otherSideProposal;
    return pickedDbValue;
}
